"use client";

import { useState } from "react";
import Image from "next/image";
import type { ContactInfo, SocialMediaLink } from "@/lib/navigation";
import type { SalesAgent } from "@/lib/contact";
import { useInvisibleRecaptcha } from "@/lib/useInvisibleRecaptcha";
import { isValidEmail, isValidPhone, sanitizePhone } from "@/lib/validators";
import { socialPlatformLabel } from "@/lib/helpers";
import "@/app/(main)/components/contact-us-card.css";

interface ContactUsCardProps {
  contactInfo: ContactInfo;
  socialMedia: SocialMediaLink[];
  salesTeam: SalesAgent[];
  successMessage?: string;
  failureMessage?: string;
  /**
   * Brand the submission is attributed to — required. Resolve from
   * `process.env.NEXT_PUBLIC_BRAND_ID_*` at the parent (section) layer so
   * the source is explicit and missing env vars surface as a clear bug
   * at call sites rather than being silently swallowed here.
   */
  brandId: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  honeypot: string;
}

const INITIAL_FORM: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
  honeypot: "",
};

export default function ContactUsCard({
  contactInfo,
  socialMedia,
  salesTeam,
  successMessage,
  failureMessage,
  brandId,
}: ContactUsCardProps) {
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<"" | "success" | "error">("");
  const { containerRef: recaptchaRef, getToken } = useInvisibleRecaptcha();

  const update = (field: keyof FormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const isValid =
    form.firstName &&
    form.lastName &&
    isValidEmail(form.email) &&
    isValidPhone(form.phone) &&
    form.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Refuse to submit without a brand — leads would land in an unknown
    // bucket on the CRM side, and we'd rather surface a clear UI error
    // than file the form silently.
    if (!brandId) {
      setResult("error");
      return;
    }

    setSubmitting(true);
    setResult("");

    try {
      // Runs the invisible challenge. Resolves "" when reCAPTCHA is unavailable
      // (script blocked, no site key) — the route handler decides whether that
      // is acceptable, so the rule lives on the server only.
      const recaptchaToken = await getToken();

      // Same-origin, so this deliberately uses fetch rather than the shared
      // axios client — no CRM base URL or bearer token belongs on this call.
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brandId,
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone,
          email: form.email,
          message: form.message,
          honeypot: form.honeypot,
          recaptchaToken,
        }),
      });

      if (!response.ok) throw new Error("Contact submission failed");

      setResult("success");
      setForm(INITIAL_FORM);
      setTouched({});
    } catch {
      setResult("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ══ Section 1: Contact Form + Info ══ */}
      <div className="contact-section">
        <div className="container">
          <div className="contact-section-header">
            <div className="contact-badge">
              <i className="fa-solid fa-paper-plane" /> Get In Touch
            </div>
            <h2>We&apos;d Love to Hear From You</h2>
            <p>
              Have a question or want to plan your dream vacation? Reach out and
              our travel experts will get back to you shortly.
            </p>
          </div>

          <div className="contact-card-wrapper">
            <div className="contact-card-inner">
              {/* ── Form Side ── */}
              <div className="contact-form-side">
                {result === "success" && (
                  <div className="contact-toast">
                    <div className="contact-toast-content">
                      <div className="contact-toast-icon success">
                        <i className="fa-solid fa-circle-check" />
                      </div>
                      <h4>Message Sent!</h4>
                      <p>
                        {successMessage ||
                          "Thank you! We'll contact you as soon as possible."}
                      </p>
                    </div>
                  </div>
                )}

                {result === "error" && (
                  <div className="contact-toast">
                    <div className="contact-toast-content">
                      <div className="contact-toast-icon error">
                        <i className="fa-solid fa-circle-xmark" />
                      </div>
                      <h4>Something Went Wrong</h4>
                      <p>
                        {failureMessage ||
                          "Oops! Please try again later or contact us directly."}
                      </p>
                      <button
                        className="contact-submit-btn"
                        style={{ marginTop: 16 }}
                        onClick={() => setResult("")}
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                )}

                <h3>Send Us a Message</h3>
                <p className="form-subtitle">
                  Fill out the form below and we&apos;ll respond within 24
                  hours.
                </p>

                <form onSubmit={handleSubmit} autoComplete="off">
                  {/* Honeypot */}
                  <div className="contact-honeypot">
                    <input
                      type="email"
                      name="email_address"
                      value={form.honeypot}
                      onChange={(e) => update("honeypot", e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div className="contact-form-row">
                    <div className="contact-field">
                      <label>
                        First Name <span className="required-mark">*</span>
                      </label>
                      <div className="contact-input-wrap">
                        <i className="field-icon fa-regular fa-user" />
                        <input
                          type="text"
                          placeholder="John"
                          value={form.firstName}
                          onBlur={() => touch("firstName")}
                          onChange={(e) => update("firstName", e.target.value)}
                        />
                      </div>
                      {touched.firstName && !form.firstName && (
                        <span className="field-error">
                          First name is required
                        </span>
                      )}
                    </div>

                    <div className="contact-field">
                      <label>
                        Last Name <span className="required-mark">*</span>
                      </label>
                      <div className="contact-input-wrap">
                        <i className="field-icon fa-regular fa-user" />
                        <input
                          type="text"
                          placeholder="Doe"
                          value={form.lastName}
                          onBlur={() => touch("lastName")}
                          onChange={(e) => update("lastName", e.target.value)}
                        />
                      </div>
                      {touched.lastName && !form.lastName && (
                        <span className="field-error">
                          Last name is required
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="contact-field">
                    <label>
                      Email Address <span className="required-mark">*</span>
                    </label>
                    <div className="contact-input-wrap">
                      <i className="field-icon fa-regular fa-envelope" />
                      <input
                        type="email"
                        placeholder="john@example.com"
                        value={form.email}
                        onBlur={() => touch("email")}
                        onChange={(e) => update("email", e.target.value)}
                      />
                    </div>
                    {touched.email && !form.email && (
                      <span className="field-error">
                        Email address is required
                      </span>
                    )}
                    {touched.email &&
                      form.email &&
                      !isValidEmail(form.email) && (
                        <span className="field-error">
                          Please enter a valid email address
                        </span>
                      )}
                  </div>

                  <div className="contact-field">
                    <label>
                      Phone Number <span className="required-mark">*</span>
                    </label>
                    <div className="contact-input-wrap">
                      <i className="field-icon fa-solid fa-phone" />
                      <input
                        type="tel"
                        placeholder="+1555000000"
                        value={form.phone}
                        onBlur={() => touch("phone")}
                        onChange={(e) =>
                          update("phone", sanitizePhone(e.target.value))
                        }
                      />
                    </div>
                    {touched.phone && !form.phone && (
                      <span className="field-error">
                        Phone number is required
                      </span>
                    )}
                    {touched.phone &&
                      form.phone &&
                      !isValidPhone(form.phone) && (
                        <span className="field-error">
                          Please enter a valid phone number
                        </span>
                      )}
                  </div>

                  <div className="contact-field">
                    <label>
                      Message <span className="required-mark">*</span>
                    </label>
                    <div className="contact-input-wrap">
                      <i className="field-icon fa-regular fa-message" />
                      <textarea
                        placeholder="Tell us about your dream trip..."
                        rows={4}
                        value={form.message}
                        onBlur={() => touch("message")}
                        onChange={(e) => update("message", e.target.value)}
                      />
                    </div>
                    {touched.message && !form.message && (
                      <span className="field-error">Message is required</span>
                    )}
                  </div>

                  {/* Invisible reCAPTCHA mounts here — renders no UI of its
                      own beyond the floating badge, and only surfaces a
                      challenge when Google considers the visitor suspicious. */}
                  <div ref={recaptchaRef} />

                  <button
                    type="submit"
                    className="contact-submit-btn"
                    disabled={submitting || !isValid}
                  >
                    {submitting ? (
                      <>
                        <span className="btn-spinner" /> Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" /> Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* ── Info Side ── */}
              <div className="contact-info-side">
                <h3>Contact Information</h3>
                <p className="info-subtitle">
                  Reach out through any of these channels
                </p>

                {contactInfo?.mainPhone && (
                  <div className="info-item">
                    <div className="info-item-icon">
                      <i className="fa-solid fa-phone" />
                    </div>
                    <div className="info-item-content">
                      <h5>Phone</h5>
                      <a
                        href={`tel:${contactInfo.mainPhone}`}
                        title={`Call us: ${contactInfo.mainPhone}`}
                      >
                        {contactInfo.mainPhone}
                      </a>
                    </div>
                  </div>
                )}

                {contactInfo?.email1 && (
                  <div className="info-item">
                    <div className="info-item-icon">
                      <i className="fa-solid fa-envelope" />
                    </div>
                    <div className="info-item-content">
                      <h5>Email</h5>
                      <a
                        href={`mailto:${contactInfo.email1}`}
                        title={`Email us: ${contactInfo.email1}`}
                      >
                        {contactInfo.email1}
                      </a>
                      {contactInfo?.email2 && (
                        <>
                          <br />
                          <a
                            href={`mailto:${contactInfo.email2}`}
                            title={`Email us: ${String(contactInfo.email2)}`}
                          >
                            {String(contactInfo.email2)}
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {contactInfo?.address && (
                  <div className="info-item">
                    <div className="info-item-icon">
                      <i className="fa-solid fa-location-dot" />
                    </div>
                    <div className="info-item-content">
                      <h5>Address</h5>
                      <p>{String(contactInfo.address)}</p>
                    </div>
                  </div>
                )}

                {(() => {
                  const fullAddr = (contactInfo as Record<string, unknown>)
                    ?.fullAddress;
                  if (!fullAddr || contactInfo?.address) return null;
                  return (
                    <div className="info-item">
                      <div className="info-item-icon">
                        <i className="fa-solid fa-location-dot" />
                      </div>
                      <div className="info-item-content">
                        <h5>Address</h5>
                        <p>{String(fullAddr)}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Social Links */}
                {socialMedia.length > 0 && (
                  <>
                    <div className="contact-social-divider" />
                    <div className="contact-social-list">
                      <div>
                        <h5>Follow Us</h5>
                        <div style={{ display: "flex", gap: 12 }}>
                          {socialMedia.map((s) => (
                            <a
                              key={s.id}
                              href={s.socialMediaURL}
                              target="_blank"
                              rel="noreferrer"
                              className="social-icon-link"
                              aria-label={`Central Holidays on ${socialPlatformLabel(s.socialMediaURL)}`}
                              title={`Central Holidays on ${socialPlatformLabel(s.socialMediaURL)}`}
                            >
                              <Image
                                src={s.iconURL}
                                alt=""
                                width={18}
                                height={18}
                                unoptimized
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ Section 2: Sales Team (smoke bg) ══ */}
      {salesTeam.length > 0 && (
        <div className="contact-section bg-smoke">
          <div className="container">
            <div className="sales-team-section">
              <div className="sales-team-header">
                <div className="section-badge">
                  <i className="fa-solid fa-users" /> Our Experts
                </div>
                <h3>Your Travel Consultants</h3>
                <p>Dedicated specialists ready to craft your perfect getaway</p>
              </div>
              <div className="row g-4">
                {salesTeam.map((agent) => (
                  <div key={agent.id} className="col-xl-4 col-lg-4 col-md-6">
                    <div className="sales-agent-card">
                      <div className="sales-agent-card-bg" />
                      <div className="sales-agent-avatar">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={agent.photo || "/profile.png"}
                          alt={agent.firstName}
                        />
                      </div>
                      <div className="sales-agent-body">
                        <div className="sales-agent-name">
                          {agent.firstName} {agent.lastName}
                          {agent.certificates && (
                            <span className="sales-agent-certs">
                              , {agent.certificates}
                            </span>
                          )}
                        </div>
                        {agent.title && (
                          <div className="sales-agent-title">{agent.title}</div>
                        )}
                        <ul className="sales-agent-details">
                          {agent.businessPhone && (
                            <li>
                              <div className="sales-detail-icon">
                                <i className="fa-solid fa-phone" />
                              </div>
                              <a
                                href={`tel:${agent.businessPhone}`}
                                title={`Call ${agent.firstName} ${agent.lastName}`}
                              >
                                {agent.businessPhone}
                                {agent.extension
                                  ? ` ext. ${agent.extension}`
                                  : ""}
                              </a>
                            </li>
                          )}
                          <li>
                            <div className="sales-detail-icon">
                              <i className="fa-solid fa-envelope" />
                            </div>
                            <a
                              href={`mailto:${agent.email}`}
                              title={`Email ${agent.firstName} ${agent.lastName}`}
                            >
                              {agent.email}
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ Section 3: Map ══ */}
      {contactInfo?.latitude && contactInfo?.longitude && (
        <div className="contact-section pb-0">
          <div className="container">
            <div className="contact-map-section">
              <div className="contact-map-header">
                <div className="section-badge">
                  <i className="fa-solid fa-location-dot" /> Our Location
                </div>
                <h3>Come Visit Us</h3>
                <p>We&apos;d love to welcome you at our office</p>
              </div>
              <div className="contact-map-wrapper">
                <iframe
                  src={`https://www.google.com/maps?q=${contactInfo.latitude},${contactInfo.longitude}&z=17&output=embed`}
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
